/**
 * Nine more holes, decided on a tee.
 *
 * The schedule is nine rounds long and the trip is not. Somebody looks at the
 * light at ten past four and eight men want to go again — and until now the
 * only way to record that was Setup, behind the PIN, where the draw button
 * rebuilds every foursome of the week and warns that it will unsettle every
 * signed card. Nobody was going to do that standing on the first tee.
 *
 * So this is deliberately the shortest path that is still honest: pick the
 * course, tick who is playing, and go. Three things it will not do quietly —
 *
 * The seating is SHOWN before it is created. It is the app's opinion about who
 * plays with whom, and a man reading his own name in a group is the only real
 * check on it.
 *
 * A format is only offered if the men actually going out can play it. A 2v2
 * needs whole foursomes and a singles match needs an even group, so the list is
 * computed from the SEATING rather than from the size of the field — six men
 * seated as two threes can play neither, however even six is.
 *
 * Money is not a default. A round counts only if it is deliberately switched
 * on, only by the men who run the week, and the switch says out loud what it
 * will do to the purse — because the purse balances to the dollar and a ladder
 * added on Tuesday comes out of somebody's pocket on Thursday.
 */

import { useMemo, useState } from 'react'
import { courseHandicapFor, findTee, holesFor, playingIndex } from '../engine/handicap'
import { levelSplit } from '../engine/foursomes'
import { seatRound } from '../engine/seating'
import type { Tournament } from '../engine/types'
import { FORMAT_PRESETS, configFor, type FormatPresetId } from '../data/seed'
import { playerName, todayLocal } from '../select'
import { addRound } from '../store'
import { Bar } from '../ui'

/** 'card' is the default: a scorecard with no contest on it. */
type Choice = 'card' | FormatPresetId

/** Only the three whole-nine choices: an ad-hoc round is never a picked set of holes. */
type Nine = 'front' | 'back' | 'full18'

/**
 * The championship is not something a man adds on a tee.
 *
 * Its preset carries the real flighted ladder — $4,050 against a pot that
 * balances to the dollar — and a second FLIGHTED stroke-play match is the one
 * shape the rest of the app assumes cannot exist, since that is how it knows
 * which match is the championship. The server refuses it too; this is why it is
 * never offered.
 */
const NOT_ON_A_TEE: FormatPresetId = 'strokeNet'

/** Minutes past midnight as HH:MM. The one place this arithmetic lives. */
const asClock = (minutes: number): string =>
  `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

/** HH:MM to minutes past midnight, or null when there is nothing usable there. */
const asMinutes = (clock: string): number | null => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(clock)
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

/**
 * The next ten-minute mark, which is what a tee time is.
 *
 * Defaulting to the exact current minute produced 16:07, and nobody writes that
 * on a tee sheet. Rounding up also matches what is being described: a round
 * somebody is about to start, not one that started while they typed.
 */
const nextTeeTime = (now: Date): string =>
  asClock(Math.min(Math.ceil((now.getHours() * 60 + now.getMinutes() + 1) / 10) * 10, 23 * 60 + 50))

/**
 * Which formats the men going out can actually play.
 *
 * Computed from the seating rather than the size of the field, which is the
 * only version of this that is true: six men are an even number and still
 * cannot play singles, because they go out as two threes. Quota and the bare
 * card rank whoever is there and do not care.
 */
function playableWith(groups: readonly string[][]): Set<Choice> {
  const ok = new Set<Choice>(['card', 'quotaGroup', 'quotaField'])
  if (groups.length === 0) return ok
  if (groups.every((g) => g.length % 2 === 0)) ok.add('individualMatchPlay')
  if (groups.every((g) => g.length === 4)) {
    ok.add('teamHighLow')
    ok.add('teamCombinedNet')
    ok.add('teamFourBall')
  }
  return ok
}

/**
 * The contests inside each group — which the four head-to-head formats are
 * scored from, and without which they score nothing at all.
 *
 * The split itself is `levelSplit`, shared with the seating editor: sort on
 * strokes, low with high against the two in the middle. It lived here in full
 * and again in `foursomes.ts`, which is two statements of one rule and two
 * chances to disagree about what a level 2v2 is.
 */
export function contestsFor(
  format: Choice,
  groups: readonly string[][],
  strokesOf: (id: string) => number,
): { groupIndex: number; sides: string[][] }[] {
  const kind = format === 'card' ? 'strokeNet' : configFor(format).kind
  return groups.flatMap((group, groupIndex) =>
    levelSplit(group, kind, strokesOf).map((sides) => ({ groupIndex, sides })),
  )
}

/**
 * The men allowed to add a round that counts, by id.
 *
 * An empty list is read as absent, exactly as the server reads it: taken
 * literally, an empty list locks out the only men who could edit it back.
 */
export function countingRoundNames(doc: Tournament): string[] {
  const named = doc.settings.canCountRounds
  if (named && named.length > 0) return named
  return doc.players.filter((p) => p.isAdmin).map((p) => p.id)
}

/** Whether this man may put money on a round he is adding. */
export const mayAddCountingRound = (doc: Tournament, playerId: string): boolean =>
  countingRoundNames(doc).includes(playerId)

export function AddRound({
  doc,
  me,
  onDone,
  onCancel,
}: {
  doc: Tournament
  /** The player holding the phone — decides whether money is on the table. */
  me: string
  /** Called with the new round's id once the server has it. */
  onDone: (roundId: string) => void
  onCancel: () => void
}) {
  const [courseId, setCourseId] = useState(doc.courses[0]?.id ?? '')
  const [nine, setNine] = useState<Nine>('front')
  const [teeTime, setTeeTime] = useState(() => nextTeeTime(new Date()))
  const [choice, setChoice] = useState<Choice>('card')
  const [counts, setCounts] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const field = doc.players.filter((p) => p.active)
  const [ticked, setTicked] = useState<string[]>(() => field.map((p) => p.id))
  const course = doc.courses.find((c) => c.id === courseId)
  const mayCount = mayAddCountingRound(doc, me)

  /*
   * Read back against the live roster on every render, because the roster is
   * live: the 8-second poll can retire a player while this screen is open, and
   * a stale id would then be looked up and dereferenced inside the memo below,
   * taking the whole Score tab down rather than quietly dropping a name.
   */
  const playing = ticked.filter((id) => field.some((p) => p.id === id))

  /** What each man gets round this course, for the seating and for the sides. */
  const strokesOf = useMemo(() => {
    const cache = new Map<string, number>()
    return (id: string): number => {
      if (!course) return 0
      const hit = cache.get(id)
      if (hit !== undefined) return hit
      const p = doc.players.find((x) => x.id === id)
      const ch = p
        ? courseHandicapFor(playingIndex(p), findTee(course, p), holesFor(course, nine))
        : 0
      cache.set(id, ch)
      return ch
    }
  }, [course, doc.players, nine])

  /*
   * The seating, recomputed as men are ticked on and off.
   *
   * The seed moves with the SIZE of the field, so ticking somebody off re-draws
   * the round rather than shuffling everybody who was already placed, and a
   * field of the same size always draws the same way — which keeps the list
   * still while it is being read.
   */
  const groups = useMemo(() => {
    if (!course) return []
    const seats = playing.map((id) => ({ id, courseHandicap: strokesOf(id) }))
    const history = doc.groups.map((g) => g.playerIds)
    return seatRound(seats, history, { seed: `${doc.settings.rngSeed}#adhoc#${playing.length}` })
  }, [course, doc.groups, doc.settings.rngSeed, playing, strokesOf])

  const playable = playableWith(groups)
  /*
   * A format these groups can no longer play falls back to a card rather than
   * being refused at the end. Ticking a man off after choosing is a normal
   * thing to do, and the alternative is a create button that fails on a rule
   * nobody has been shown yet.
   */
  const format: Choice = playable.has(choice) ? choice : 'card'
  const downgraded = format !== choice
  const teeMinutes = asMinutes(teeTime)
  const odd = groups.find((g) => g.length % 2 === 1)

  const create = async () => {
    if (!course || teeMinutes === null) return
    setBusy(true)
    setError(null)
    try {
      const { roundId } = await addRound({
        date: todayLocal(),
        courseId: course.id,
        nine,
        firstTeeTime: teeTime,
        teeIntervalMinutes: 10,
        // No label: the server names it the way every other round in the
        // document is named, and one naming convention beats two.
        counts: counts && mayCount,
        format,
        groups,
        pairings: contestsFor(format, groups, strokesOf),
      })
      onDone(roundId)
    } catch (e) {
      /*
       * Put in words a man on a fairway can act on. The server's own refusals
       * are already sentences and are shown as they are; what has to be
       * translated is the network, which says "Load failed" or "The operation
       * was aborted due to timeout" — true, and no use to anybody.
       */
      const raw = e instanceof Error ? e.message : ''
      setError(
        !navigator.onLine || /fetch|network|load failed|abort|timeout/i.test(raw)
          ? 'No signal reached the server, so the round was not added. Try again when you have a bar.'
          : // The version check's own word for it, which means nothing to
            // anybody standing on a tee.
            raw === 'conflict'
            ? 'Somebody changed the week while you were on this screen. Try again.'
            : raw || 'That did not save. Try again.',
      )
      setBusy(false)
    }
  }

  return (
    <>
      <Bar kicker="Not on the schedule" title="Add a round" />

      <div className="px-4 py-3 border-b border-rule grid gap-2">
        <select
          value={courseId}
          onChange={(e) => setCourseId(e.target.value)}
          aria-label="Course"
          className="bc-label border border-ink px-2 py-2 bc-tap bg-paper w-full"
        >
          {doc.courses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.verified ? '' : ' (unverified)'}
            </option>
          ))}
        </select>
        <div className="grid grid-cols-2 gap-2">
          <select
            value={nine}
            onChange={(e) => setNine(e.target.value as Nine)}
            aria-label="Which holes"
            className="bc-label border border-ink px-2 py-2 bc-tap bg-paper"
          >
            <option value="front">Front 9</option>
            <option value="back">Back 9</option>
            <option value="full18">All 18</option>
          </select>
          <input
            type="time"
            value={teeTime}
            onChange={(e) => setTeeTime(e.target.value)}
            aria-label="First tee time"
            className="bc-label border border-ink px-2 py-2 bc-tap bg-paper"
          />
        </div>
      </div>

      {/* Who is going out. Everyone is ticked, because unticking the three men
          in the bar is faster than ticking the nine who are not. */}
      <div className="px-4 py-3 border-b border-rule">
        <div className="flex items-baseline justify-between">
          <span className="bc-label">Playing</span>
          <span className="text-[13px] text-mute tabular-nums">
            {playing.length} of {field.length}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mt-2">
          {field.map((p) => {
            const on = playing.includes(p.id)
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setTicked((cur) =>
                    cur.includes(p.id) ? cur.filter((x) => x !== p.id) : [...cur, p.id],
                  )
                }
                className={`bc-tap bc-label py-2 px-1 border-2 border-ink ${
                  on ? 'bg-ink text-paper' : 'bg-paper text-mute'
                }`}
              >
                {p.displayName}
              </button>
            )
          })}
        </div>
      </div>

      {/* The app's opinion about the foursomes, before it is a fact. */}
      <div className="px-4 py-3 border-b border-rule">
        <span className="bc-label">Going out</span>
        {groups.length === 0 ? (
          <p className="text-[13px] text-dim mt-1">Nobody is playing yet.</p>
        ) : (
          <ul className="mt-1.5 space-y-1">
            {groups.map((g, i) => (
              <li key={g.join('|')} className="text-[13px] flex gap-2">
                <span className="bc-num text-signal shrink-0 tabular-nums">
                  {teeMinutes === null ? '--:--' : asClock(teeMinutes + i * 10)}
                </span>
                <span className="min-w-0">{g.map((id) => playerName(doc, id)).join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-4 py-3 border-b border-rule">
        <span className="bc-label">Playing for</span>
        <select
          value={format}
          onChange={(e) => setChoice(e.target.value as Choice)}
          aria-label="Format"
          className="bc-label border border-ink px-2 py-2 bc-tap bg-paper w-full mt-1.5"
        >
          <option value="card">Just a card — no contest</option>
          {FORMAT_PRESETS.filter((f) => f.id !== NOT_ON_A_TEE && playable.has(f.id)).map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        {/*
          Why a format is missing, said in terms of the men on the screen rather
          than in terms of a rule. The list shrinks as men are ticked off, and a
          dropdown that quietly loses its options is how somebody decides the
          app is broken.
        */}
        {!playable.has('teamHighLow') && (
          <p className="text-[12px] text-mute leading-snug mt-1.5">
            {odd
              ? `A group of ${odd.length} cannot be split into sides, so only quota and a plain card are on offer.`
              : 'The team formats need whole foursomes, so they are not offered to these groups.'}
          </p>
        )}
        {downgraded && (
          <p className="text-[12px] text-signal leading-snug mt-1.5">
            These groups cannot play that format any more, so this is a plain card unless
            you pick again.
          </p>
        )}
      </div>

      {/*
        Money, last and off by default.

        A man adding nine holes at dusk is not asking to move the championship
        seeding, and the failure mode is not symmetric: a fun round wrongly
        counted rewrites the standings and the purse, while a counting round
        wrongly left as fun can simply be added again.
      */}
      <div className="px-4 py-3 border-b border-rule">
        <button
          type="button"
          disabled={!mayCount}
          aria-pressed={counts && mayCount}
          onClick={() => setCounts((v) => !v)}
          className={`bc-tap w-full border-2 border-ink bc-label py-3 ${
            counts && mayCount ? 'bg-signal text-paper' : 'bg-paper'
          } ${mayCount ? '' : 'opacity-40'}`}
        >
          {counts && mayCount ? 'Counts towards the tournament' : 'Just for fun'}
        </button>
        <p className="text-[13px] text-mute leading-snug mt-2">
          {!mayCount ? (
            <>
              Anyone can add a round for fun. Only{' '}
              {countingRoundNames(doc)
                .map((id) => playerName(doc, id))
                .join(', ')}{' '}
              can add one that counts.
            </>
          ) : counts ? (
            <>
              Points, cash and championship seeding — and only for the {playing.length} men
              going out. Check Payouts first: the pot is balanced to the dollar.
            </>
          ) : (
            'Scored, carded and open to side bets, but it pays nothing and moves nothing. Nobody has to sign for it.'
          )}
        </p>
      </div>

      {error && (
        <p className="px-4 py-3 text-[13px] text-signal leading-snug border-b border-rule">
          {error}
        </p>
      )}

      <div className="px-4 py-4 grid gap-2">
        <button
          type="button"
          disabled={busy || groups.length === 0 || !course || teeMinutes === null}
          onClick={create}
          className="bc-tap w-full bg-signal text-paper bc-label py-4 disabled:opacity-40"
        >
          {busy ? 'Adding…' : 'Add the round'}
        </button>
        {teeMinutes === null && (
          <p className="text-[13px] text-signal leading-snug">A round needs a tee time.</p>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="bc-tap w-full border-2 border-ink bc-label py-3"
        >
          Cancel
        </button>
        <p className="text-[13px] text-mute leading-snug">
          This needs a signal — the round has to exist on the server before anybody can
          post a score to it. An admin can delete it again under Setup.
        </p>
      </div>
    </>
  )
}
